import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ChunkErrorFallback } from './ChunkErrorFallback';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically for catching chunk loading errors
 * from lazy-loaded components. Shows a user-friendly refresh UI.
 */
class ChunkErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log chunk errors for debugging
    console.error('[ChunkErrorBoundary] Caught error:', error);
    console.error('[ChunkErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <ChunkErrorFallback 
          error={this.state.error || undefined} 
          resetErrorBoundary={this.handleReset} 
        />
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
